import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc } from "~/api/trpc";
import { Language, useLanguageStore } from "~/stores/language-store";

type PageState = 'processing' | 'success' | 'cancelled' | 'error';

const formatEur = (cents: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const KlarnaAuthorizationComplete = () => {
    const [searchParams] = useSearchParams();
    const { language } = useLanguageStore();
    const isGerman = language === Language.GERMAN;
    const [state, setState] = useState<PageState>('processing');
    const [amountCents, setAmountCents] = useState<number | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const confirmedRef = useRef(false);

    const confirmMutation = trpc.publicPayments.confirmBillingDraftKlarnaAuthorization.useMutation();

    useEffect(() => {
        if (confirmedRef.current) return;
        confirmedRef.current = true;

        if (searchParams.get('cancelled') === '1') {
            setState('cancelled');
            return;
        }

        const klarnaAuthorizationId = searchParams.get('auth_id');
        const sessionId = searchParams.get('session_id');
        if (!klarnaAuthorizationId || !sessionId) {
            setState('error');
            setErrorMessage(isGerman ? 'Fehlende Autorisierungsinformationen.' : 'Missing authorization information.');
            return;
        }

        confirmMutation.mutate(
            { klarnaAuthorizationId, sessionId },
            {
                onSuccess: (result) => {
                    if (result.success) {
                        setAmountCents(result.amountCents);
                        setState('success');
                    } else {
                        setState('error');
                        setErrorMessage(result.error);
                    }
                },
                onError: (err) => {
                    setState('error');
                    setErrorMessage(err.message || (isGerman ? 'Ein Fehler ist aufgetreten.' : 'An error occurred.'));
                },
            }
        );
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center px-4">
            <div className="max-w-md w-full text-center flex flex-col items-center gap-6">
                {state === 'processing' && (
                    <>
                        <Loader2 className="w-12 h-12 text-burgundy animate-spin" />
                        <h2 className="text-xl font-medium text-burgundy">
                            {isGerman ? 'Deine Klarna-Autorisierung wird bestätigt...' : 'Confirming your Klarna authorization...'}
                        </h2>
                    </>
                )}

                {state === 'success' && (
                    <>
                        <CheckCircle className="w-12 h-12 text-green-600" />
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-medium text-burgundy">
                                {isGerman ? 'Vielen Dank!' : 'Thank you!'}
                            </h2>
                            <p className="text-sm text-burgundy/60 font-light">
                                {isGerman
                                    ? `Deine Klarna-Autorisierung${amountCents !== null ? ` über ${formatEur(amountCents)}` : ''} war erfolgreich. Du kannst dieses Fenster schließen.`
                                    : `Your Klarna authorization${amountCents !== null ? ` of ${formatEur(amountCents)}` : ''} was successful. You can close this window.`}
                            </p>
                        </div>
                    </>
                )}

                {state === 'cancelled' && (
                    <>
                        <XCircle className="w-12 h-12 text-burgundy/60" />
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-medium text-burgundy">
                                {isGerman ? 'Autorisierung abgebrochen' : 'Authorization cancelled'}
                            </h2>
                            <p className="text-sm text-burgundy/60 font-light">
                                {isGerman
                                    ? 'Die Klarna-Autorisierung wurde abgebrochen. Du kannst den Link erneut öffnen, solange er gültig ist.'
                                    : 'The Klarna authorization was cancelled. You can open the link again while it is still valid.'}
                            </p>
                        </div>
                    </>
                )}

                {state === 'error' && (
                    <>
                        <XCircle className="w-12 h-12 text-red-500" />
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-medium text-burgundy">
                                {isGerman ? 'Etwas ist schiefgelaufen' : 'Something went wrong'}
                            </h2>
                            <p className="text-sm text-red-600 font-light">{errorMessage}</p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default KlarnaAuthorizationComplete;
